/**
 * Reusable dashboard bar chart (MUI). Supports vertical/horizontal, stacked.
 * dataValues: { series: [{ name, data }], categories: string[] }
 */
import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { useTheme } from '@mui/material/styles';

export default function DashboardBarChart({ title, dataValues, height = 300, stacked = false, horizontal = false }) {
  const theme = useTheme();
  const palette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];

  const categories = Array.isArray(dataValues?.categories) ? dataValues.categories : [];
  const series = Array.isArray(dataValues?.series) ? dataValues.series : [];
  const hasData = series.some((s) => Array.isArray(s?.data) && s.data.some((v) => Number(v) > 0));

  const chartSeries = series.map((s, i) => ({
    id: s?.name || `series-${i}`,
    label: s?.name || `Series ${i + 1}`,
    data: Array.isArray(s?.data) ? s.data : [],
    stack: stacked ? 'total' : undefined,
  }));

  if (!hasData && categories.length === 0) {
    return (
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
        {title && (
          <Typography component="h2" variant="subtitle2" fontWeight={600} gutterBottom>
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          No data
        </Typography>
        </CardContent>
      </Card>
    );
  }

  if (categories.length === 0) {
    return (
      <Card variant="outlined" sx={{ width: '100%' }}>
        <CardContent>
        {title && (
          <Typography component="h2" variant="subtitle2" fontWeight={600} gutterBottom>
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          No data
        </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        {title && (
          <Typography component="h2" variant="subtitle2" fontWeight={600} gutterBottom>
            {title}
          </Typography>
        )}
        <BarChart
          borderRadius={6}
          colors={palette}
          xAxis={
            horizontal
              ? [{ id: 'x', scaleType: 'linear', min: 0 }]
              : [{ scaleType: 'band', data: categories, categoryGapRatio: 0.5 }]
          }
          yAxis={
            horizontal
              ? [{ id: 'y', scaleType: 'band', data: categories, categoryGapRatio: 0.5, barGapRatio: 0.1 }]
              : [{ width: 50 }]
          }
          series={chartSeries}
          height={height}
          margin={{ left: horizontal ? 100 : 0, right: 16, top: 16, bottom: 24 }}
          layout={horizontal ? 'horizontal' : 'vertical'}
          grid={{ horizontal: true, vertical: false }}
          slotProps={{
            legend: { hidden: series.length <= 1 },
          }}
        />
      </CardContent>
    </Card>
  );
}
